/**
 * Pure helpers for public evidence manifest verification (WebCrypto RSASSA-PKCS1-v1_5).
 */

export function pemToSpki(pem: string): ArrayBuffer {
  const b64 = pem
    .replace(/-----BEGIN [^-]+-----/, '')
    .replace(/-----END [^-]+-----/, '')
    .replace(/\s+/g, '')
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

export function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/\s+/g, '')
  const binary = atob(clean)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

export async function verifyEvidenceManifestWebCrypto(
  manifestBytes: Uint8Array,
  signatureB64: string,
  pem: string,
): Promise<boolean> {
  try {
    const spkiBuffer = pemToSpki(pem)
    const cryptoKey = await crypto.subtle.importKey(
      'spki',
      spkiBuffer,
      {
        name: 'RSASSA-PKCS1-v1_5',
        hash: 'SHA-256',
      },
      false,
      ['verify'],
    )
    const sigBytes = base64ToBytes(signatureB64)
    return await crypto.subtle.verify(
      'RSASSA-PKCS1-v1_5',
      cryptoKey,
      sigBytes as unknown as BufferSource,
      manifestBytes as unknown as BufferSource,
    )
  } catch {
    return false
  }
}

export function extractProjectFromKeyVersion(keyVersion: string): string | null {
  const match = keyVersion.match(/^projects\/([^/]+)/)
  return match ? match[1] : null
}

export function buildVerificationKeyEndpoint(
  keyVersion: string,
  region: string = 'asia-south1',
): string {
  const explicitUrl = import.meta.env.VITE_EVIDENCE_KEY_URL as string | undefined
  if (explicitUrl) {
    const joiner = explicitUrl.includes('?') ? '&' : '?'
    return `${explicitUrl}${joiner}keyVersion=${encodeURIComponent(keyVersion)}`
  }
  const projectId = extractProjectFromKeyVersion(keyVersion) || 'timelining-sit'
  return `https://${region}-${projectId}.cloudfunctions.net/getEvidenceVerificationKey?keyVersion=${encodeURIComponent(keyVersion)}`
}

export function validateManifestFiles(files: unknown[]): { valid: boolean; reason?: string } {
  if (!Array.isArray(files)) {
    return { valid: false, reason: 'Manifest files list is missing or invalid' }
  }
  const sha256Regex = /^[0-9a-fA-F]{64}$/
  for (const f of files) {
    if (!f || typeof f !== 'object') {
      return { valid: false, reason: 'Invalid manifest file entry' }
    }
    const entry = f as { path?: unknown; sha256?: unknown }
    if (typeof entry.path !== 'string' || typeof entry.sha256 !== 'string') {
      return { valid: false, reason: 'Manifest file entry missing path or sha256' }
    }
    if (/[\r\n]/.test(entry.path)) {
      return { valid: false, reason: 'File path contains newline' }
    }
    if (/^[/\\]/.test(entry.path)) {
      return { valid: false, reason: 'File path cannot be absolute or start with a slash' }
    }
    if (/(^|[/\\])\.\.([/\\]|$)/.test(entry.path)) {
      return { valid: false, reason: 'File path cannot contain parent directory traversal (..)' }
    }
    if (!sha256Regex.test(entry.sha256)) {
      return { valid: false, reason: 'File sha256 must be exactly 64 hexadecimal characters' }
    }
  }
  return { valid: true }
}

export function generateSha256SumsText(files: Array<{ path: string; sha256: string }>): string {
  if (!files || files.length === 0) return ''
  return files.map((f) => `${f.sha256}  ${f.path}`).join('\n') + '\n'
}

export const FIXED_SHASUM_COMMAND = 'shasum -a 256 -c SHA256SUMS'

export function generateShasumCheckCommand(files: Array<{ path: string; sha256: string }>): string {
  if (!files || files.length === 0) return ''
  return files.map((f) => `echo "${f.sha256}  ${f.path}" | shasum -a 256 -c`).join(' && ')
}
